import { authApi } from '@/api/auth';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  Input,
} from '@/components/ui';
import { loginErrorInfo, type LoginErrorInfo } from '@/lib/login-error';
import { useAuthStore } from '@/stores/auth-store';
import { useTranslation } from '@kgc/i18n';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useAuthStore(state => state.login);

  const [email, setEmail] = useState(import.meta.env.DEV ? 'admin@kgc.hu' : '');
  const [password, setPassword] = useState(import.meta.env.DEV ? 'admin123' : '');
  const [errorInfo, setErrorInfo] = useState<LoginErrorInfo | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorInfo(null);
    setLoading(true);

    try {
      const response = await authApi.login({ email, password });
      // M-005: refreshToken is now in httpOnly cookie — only access token + user stored locally
      login(response.accessToken, response.user);
      navigate('/dashboard');
    } catch (err) {
      setErrorInfo(loginErrorInfo(err, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="mb-4">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-md bg-primary/10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
          </div>
          <img src="/rpi-logo.png" alt="rPI ERP" className="mx-auto h-16 w-auto dark:invert" />
          <CardDescription>{t('login.systemName')}</CardDescription>
          {import.meta.env.DEV && (
            <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
              DEV
            </span>
          )}
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {errorInfo && (
              <div
                data-testid="login-error"
                data-severity={errorInfo.severity}
                role="alert"
                className={
                  errorInfo.severity === 'warning'
                    ? 'rounded-md bg-amber-50 dark:bg-amber-900/30 p-3 text-sm text-amber-700 dark:text-amber-300'
                    : 'rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-600 dark:text-red-400'
                }
              >
                {errorInfo.message}
              </div>
            )}

            <Input
              label={t('login.email')}
              type="email"
              name="email"
              placeholder={t('login.placeholder.email')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
            />

            <Input
              label={t('login.password')}
              type="password"
              name="password"
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 dark:border-slate-600 dark:bg-slate-700"
                />
                <span className="text-gray-600 dark:text-gray-400">{t('login.rememberMe')}</span>
              </label>
              <a href="/forgot-password" className="text-primary hover:underline">
                {t('login.forgotPassword')}
              </a>
            </div>
          </CardContent>

          <CardFooter className="flex-col gap-4">
            <Button type="submit" className="w-full" loading={loading}>
              {t('login.submit')}
            </Button>

            {import.meta.env.DEV && (
              <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                <p className="rounded-md bg-amber-50 dark:bg-amber-900/30 p-2 text-amber-700 dark:text-amber-300">
                  <strong>{t('login.testCredentials')}:</strong> admin@kgc.hu / admin123
                </p>
              </div>
            )}
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
